import { Injectable } from '@angular/core';
import { Account, User } from '@mitel/cloudlink-sdk';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { AppInjector } from '../../app-injector';
import { ProgressIndicatorPopupComponent } from '../../shared/components/progress-indicator-popup/progress-indicator-popup.component';
import { UserBulkProduct, UserBulkProductItemServiceInf } from '../interfaces/user-prod-bulk-edit-interfaces';
import { Util } from '../utils/Util';
import { SmsNumbersMultiEditDialogComponent } from 'src/app/modules/users-multi-edit/components/user-multi-edit-dialogs/sms-numbers-multi-edit-dialog/sms-numbers-multi-edit-dialog.component';
import { IntegTwilioSmsNumbersService } from './integ-twilio-sms-numbers.service';

export enum SmsNumberOperation {
  assignAll = 'assignAll',
  unassignAll = 'unassignAll',
  unchanged = 'unchanged',
}

@Injectable({
  providedIn: 'root'
})
export class UsersBulkEditSmsNumbersService implements UserBulkProductItemServiceInf {
  private _integTwilioSmsNumbersService: IntegTwilioSmsNumbersService;
  private get integTwilioSmsNumbersService(): IntegTwilioSmsNumbersService {
    this._integTwilioSmsNumbersService ??= AppInjector.injector.get(IntegTwilioSmsNumbersService);
    return this._integTwilioSmsNumbersService;
  };
  private _translateService: TranslateService;
  private get translateService(): TranslateService {
      this._translateService ??= AppInjector.injector.get(TranslateService);
      return this._translateService;
  };
  private _modalService: NgbModal;
  private get modalService(): NgbModal {
    this._modalService ??= AppInjector.injector.get(NgbModal);
    return this._modalService;
  };

  constructor() { }

  async shouldBeDisplayed(accountId: string): Promise<boolean> {
    return this.integTwilioSmsNumbersService.isIntegrationStateOn({accountId} as Account);
  }

  async getRemainingLicenses(accountId: string): Promise<any> {
    return this.integTwilioSmsNumbersService.getListOfAvailableSmsNumbers()
    .then(availableNumbers => availableNumbers?.length || 0);
  }

  async setRequiredData(accountId: string) {
    this.integTwilioSmsNumbersService.connectionId = await this.integTwilioSmsNumbersService.getTwilioConnectionIdFromTags(accountId);
  }

  getBulkEditDialog(accountId: string, userBulkProd: UserBulkProduct): Promise<any> {
    return Promise.resolve(SmsNumbersMultiEditDialogComponent);
  }

  setProgressDataTitleInformation(progressData: any, operation: SmsNumberOperation) {
    if (operation === SmsNumberOperation.assignAll) {
      progressData.title = 'users_multi_edit.twilioSmsNumbers.assign_title';
      progressData.actionString = 'users_multi_edit.twilioSmsNumbers.assigning_number_action';
    } else if (operation === SmsNumberOperation.unassignAll) {
      progressData.title = 'users_multi_edit.twilioSmsNumbers.unassign_title';
      progressData.actionString = 'users_multi_edit.twilioSmsNumbers.unassigning_number_action';
    }
  }

  async saveUserTwilioSmsNumberBulkOperation(accountId: string, users: User[], operation: SmsNumberOperation) {
    let currentUsersList = [...users];
    try
		{
			console.log("Going to assign/unassign Twilio SMS Numbers to specified users ", users.length)

			let progressUpdate = new Subject();

			let progressData = {
        total: 0,
        done: 0,
        step: undefined,
        errorMessage: '',
        successMessage: undefined,
        updateSubject: progressUpdate
			}
			let modal = this.modalService.open(ProgressIndicatorPopupComponent, {
				backdrop: 'static',
				keyboard: false,
				centered: true,
			});

			modal.componentInstance.progressData = progressData;
      
      await this.updateTwilioSmsNumberForUsers(accountId, currentUsersList, operation, progressData);

      progressData.successMessage = this.translateService.instant("users_multi_edit.process_complete" );
			progressUpdate.next( progressData );
			return modal.result.then(result => {
				modal = null;
				progressUpdate = null;
				return;
			});
    } catch(err) {
      console.log("There was an error in the Twilio SMS number bulk operation", Util.extractMessageFromError(err));
    }
  }

  async updateTwilioSmsNumberForUsers(accountId: string, users: User[], operation: SmsNumberOperation, progressData: any) {
    this.setProgressDataTitleInformation(progressData, operation);
    progressData.total = users.length;
    progressData.done = 0;
    progressData.updateSubject.next(progressData);
    let failedUsers = [];
    if (operation === SmsNumberOperation.unassignAll) {
      for (let i = 0; i < users.length; i++) {
        await Util.timeout(50);
        await this.integTwilioSmsNumbersService.updateMiCollabSMSNumber(accountId, users[i].userId, users[i].tags, '')
        .catch(error => {
          failedUsers.push(users[i]);
        });
      }
      progressData.done++;
      progressData.updateSubject.next(progressData);
    } else if (operation === SmsNumberOperation.assignAll) {
      let availableNumbers = await this.integTwilioSmsNumbersService.getListOfAvailableSmsNumbers();
      let j = 0; // index for availableNumbers

      // Assigning a Twilio SMS number to the user
      for (let i = 0; i < users.length; i++) {
        if (users[i].tags?.smsIntegration?.number) {
          // user already has a Twilio SMS number assigned, skip it
          progressData.done++;
          progressData.updateSubject.next(progressData);
          continue;
        }

        try {
          await Util.timeout(50);
          await this.integTwilioSmsNumbersService.updateMiCollabSMSNumber(accountId, users[i].userId, users[i].tags, availableNumbers[j])
          j++;
          console.log("Update user Twilio SMS Number succeeded");
        } catch (err) {
          if (err.statusCode == 500 || Util.extractMessageFromError(err)?.indexOf("Failed to fetch") != -1) {
            try {
              // this error can occur if the system is too loaded. We will try again once before we declare it an error
              await Util.timeout(100);
              await this.integTwilioSmsNumbersService.updateMiCollabSMSNumber(accountId, users[i].userId, users[i].tags, availableNumbers[j])
              j++;
              console.log("Second attempt worked!");
            } catch (err2) {
              //failed again. We will just handle it as an error
              console.log("Second attempt to assign Twilio SMS Number failed for user", users[i].userId, Util.extractMessageFromError(err2))
              failedUsers.push(users[i]);
            }
          } else {
            failedUsers.push(users[i]);
            console.log("Failed to assign Twilio SMS Number to user", users[i].userId, Util.extractMessageFromError(err));
          }
        }

        progressData.done++;
        progressData.updateSubject.next(progressData);
      }
    }
    this.integTwilioSmsNumbersService.clearCache();
    let feedbackMsg = '';
    let feedbackErrorMsg = '';
    if (failedUsers.length > 0) {
      if (operation === SmsNumberOperation.unassignAll) {
        progressData.errorMessage += feedbackMsg + (progressData.actionString ? this.translateService.instant(progressData.actionString) : '') + "<br>";
        let message = this.translateService.instant('users_multi_edit.twilioSmsNumbers.fail_unassign', {
          failed: failedUsers.length,
          names: this.getNamesFromUsers(failedUsers)
        });
        feedbackErrorMsg += feedbackMsg + message;
        progressData.errorMessage += message + "<br>";
      } else if (failedUsers.length > 0) {
        progressData.errorMessage += feedbackMsg + (progressData.actionString ? this.translateService.instant(progressData.actionString) : '') + "<br>";
        let message = this.translateService.instant('users_multi_edit.twilioSmsNumbers.fail_assign', {
          failed: failedUsers.length,
          names: this.getNamesFromUsers(failedUsers)
        });
        feedbackErrorMsg += feedbackMsg + message;
        progressData.errorMessage += message + "<br>";
      }
    } else {
      feedbackMsg += this.translateService.instant('users_multi_edit.success_message');
    }
    // there is a ~2s delay before the numbers are updated in the backend
    // so we need to wait before allowing the user to close the dialog
    await Util.timeout(2000);
    progressData.updateSubject.next(progressData);
  }
 
  getNamesFromUsers(users: User[]) {
    let names = '';
    let i;
    for( i = 0; i< users.length-1; i++) {
      names+= (users[i].displayName ? users[i].displayName: users[i].email) + ', ';
    }
    names+= users[i].displayName ? users[i].displayName: users[i].email;
    return names;
  }
}
